import { useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { apiRequest } from "../../../../../shared/api/client";
import type { RequestDetailData } from "../../../domain/request-detail-types";
import type { RequestDetailScope } from "./useRequestDetailScope";

type Form = {title: string; description: string; category: string; quantity: string; unit: string; due: string; priority: string};
const emptyForm = (): Form => ({title: "", description: "", category: "", quantity: "1", unit: "adet", due: "", priority: "NORMAL"});
type State = {scope: RequestDetailScope; editing: boolean; form: Form};
export function useRequestEditWorkspace({scope, detail, setDetail}: {scope: RequestDetailScope; detail: RequestDetailData | null; setDetail: Dispatch<SetStateAction<RequestDetailData | null>>}) {
  const [state, setState] = useState<State>({scope, editing: false, form: emptyForm()});
  const value = state.scope === scope ? state : {scope, editing: false, form: emptyForm()};
  const revision = useRef(0), busy = useRef(new Set<RequestDetailScope>());
  const renderedRevision = revision.current;
  const [activity, setActivity] = useState<{scope: RequestDetailScope; saving: boolean}>({scope, saving: false});
  const current = () => scope.isCurrent() && detail?.id === scope.requestNo;
  const change = (update: (state: State) => State) => {
    if (!current()) return;
    revision.current++;
    setState(previous => update(previous.scope === scope ? previous : {scope, editing: false, form: emptyForm()}));
  };
  const setEditing: Dispatch<SetStateAction<boolean>> = next => change(previous => ({...previous, editing: typeof next === "function" ? next(previous.editing) : next}));
  const setEditForm: Dispatch<SetStateAction<Form>> = next => change(previous => ({...previous, form: typeof next === "function" ? next(previous.form) : next}));
  const beginEditing = () => {
    if (!current() || !detail) return;
    change(previous => ({...previous, editing: true, form: {title: detail.title, description: detail.description, category: detail.category, quantity: String(detail.quantity), unit: detail.unit, due: detail.neededAt, priority: detail.priority === "Kritik" ? "CRITICAL" : detail.priority === "Yüksek" ? "HIGH" : "NORMAL"}}));
  };
  const saveRequest = async () => {
    if (!current() || renderedRevision !== revision.current || busy.current.has(scope)) return;
    const quantity = Number(value.form.quantity);
    if (!value.form.title.trim() || !value.form.description.trim() || !value.form.category.trim() || !value.form.due.trim() || !Number.isFinite(quantity) || quantity <= 0) {
      scope.notify("Ürün, açıklama, miktar, kategori ve ihtiyaç tarihi zorunludur."); return;
    }
    busy.current.add(scope); setActivity({scope, saving: true});
    try {
      const updated = await apiRequest<RequestDetailData>(`/api/requests/${encodeURIComponent(scope.requestNo)}`, {method: "PATCH", body: JSON.stringify({...value.form, quantity})});
      if (!scope.isCurrent()) return;
      if (!updated || updated.id !== scope.requestNo) throw new Error("Güncellenen talep doğrulanamadı; kaydı yenileyip kontrol edin.");
      setDetail(updated);
      if (renderedRevision === revision.current) setEditing(false);
      scope.notify(updated.status === "Satın Almaya iletildi" ? "Talep güncellendi ve Satın Alma’ya yeniden iletildi." : "Talep güncellendi.");
    } catch (error) {scope.notify(error instanceof Error ? error.message : "Talep güncellenemedi.");}
    finally {busy.current.delete(scope); if (scope.isCurrent()) setActivity({scope, saving: false});}
  };
  return {editing: value.editing, editForm: value.form, setEditing, setEditForm, beginEditing, saveRequest, saving: activity.scope === scope && activity.saving};
}
